import React, { useCallback, useEffect, useRef, useState } from 'react';

import { StreamingProviders } from '../interfaces';
import useAsyncEffect from 'use-async-effect';
import EventsService from '@services/EventsService';
import MytaStreamsService from '@services/MytaStreamsService';
import {
  IGetStreamingProviderResponse,
  IPixelStreamingClient,
} from '@interfaces/event';
import { useMytaverse } from '@providers/MytaverseProvider';
import { LeaveEventReason } from '@providers/MytaverseProvider/interfaces';
import { AnalyticsType } from '@interfaces/analytics';

interface IPixelStreamingProviderProps {
  currentEventId?: string;
  trackEventAnalytics: (type: AnalyticsType, payload?: any) => void;
}

// Events whose bound pixel-streaming client is a native-Linux myta build are
// seated on our own myta-streams GPU nodes; spatial-manager's streaming-provider
// endpoint does not resolve NATIVE_PIXEL_STREAMING clients yet.
const NATIVE_PIXEL_STREAMING_CLIENT_TYPE = 'NATIVE_PIXEL_STREAMING';

const resolveMytaPixelStreamingClient = async (
  eventId?: string,
): Promise<IPixelStreamingClient | null> => {
  if (!eventId) {
    return null;
  }

  if (!MytaStreamsService.isNativePixelStreamingEnabled()) {
    return null;
  }

  const eventDetails = await EventsService.getEventDetails(eventId);
  const pixelStreamingClient = eventDetails?.gamecastPixelStreamingClient;

  if (
    pixelStreamingClient &&
    String(pixelStreamingClient.clientType) ===
      NATIVE_PIXEL_STREAMING_CLIENT_TYPE
  ) {
    return pixelStreamingClient;
  }

  return null;
};

export const usePixelStreamingProvider = ({
  currentEventId,
  trackEventAnalytics,
}: IPixelStreamingProviderProps) => {
  const { navigateToEventsPage, userId } = useMytaverse();

  const [streamingProvider, setStreamingProvider] =
    React.useState<StreamingProviders | null>(null);

  const [pixelStreamingClient, setPixelStreamingClient] =
    useState<IPixelStreamingClient | null>(null);

  const [isNativeProviderCheckPending, setIsNativeProviderCheckPending] =
    useState(true);

  const totalRetriesToLoadPixelStreamingProvider = useRef(0);
  // increasing capacity for stream group can take up to 10 minutes
  const maxRetryTimeInMinutes = 10;
  const retryStartTime = useRef<number | null>(null);
  const loadPixelStreamingProviderTimeout = useRef<NodeJS.Timeout | null>(null);

  const loadPixelStreamingProvider =
    useCallback(async (): Promise<IGetStreamingProviderResponse | null> => {
      if (!currentEventId) {
        return null;
      }

      const loadPixelStreamingProvider = async (resolve: any) => {
        if (!retryStartTime.current) {
          retryStartTime.current = Date.now();
        }

        loadPixelStreamingProviderTimeout.current = null;
        totalRetriesToLoadPixelStreamingProvider.current += 1;
        const pixelStreamingProviderResponse =
          await EventsService.getEventStreamingProvider(currentEventId);

        if (pixelStreamingProviderResponse?.pixelStreamingClient) {
          return resolve(pixelStreamingProviderResponse);
        }

        const elapsedTimeInMinutes =
          (Date.now() - retryStartTime.current) / 60000;
        if (elapsedTimeInMinutes > maxRetryTimeInMinutes) {
          console.log('Max retry time reached, stopping further attempts.');
          return resolve(pixelStreamingProviderResponse);
        }

        // after 5 retries, delay fixed to 10 sec
        const delay =
          totalRetriesToLoadPixelStreamingProvider.current > 5
            ? 10000
            : totalRetriesToLoadPixelStreamingProvider.current * 1000;
        console.log(
          `Retrying to get pixel streaming provider with ${delay}ms delay`,
        );

        loadPixelStreamingProviderTimeout.current = setTimeout(
          () => loadPixelStreamingProvider(resolve),
          delay,
        );
      };

      totalRetriesToLoadPixelStreamingProvider.current = 0;
      retryStartTime.current = null;

      return new Promise(loadPixelStreamingProvider);
    }, [currentEventId]);

  useAsyncEffect(async () => {
    if (!currentEventId || !userId) {
      return;
    }

    console.log('Loading pixel streaming provider');

    const mytaPixelStreamingClient = await resolveMytaPixelStreamingClient(
      currentEventId,
    );

    setIsNativeProviderCheckPending(false);

    if (mytaPixelStreamingClient) {
      setStreamingProvider(StreamingProviders.MytaStreams);
      setPixelStreamingClient(mytaPixelStreamingClient);
      console.log(
        `Selected ${StreamingProviders.MytaStreams} pixel streaming provider (event client ${mytaPixelStreamingClient.clientId} is ${NATIVE_PIXEL_STREAMING_CLIENT_TYPE})`,
      );

      return;
    }

    const { streamingProvider, pixelStreamingClient }: any =
      await loadPixelStreamingProvider();

    if (!streamingProvider) {
      trackEventAnalytics(AnalyticsType.EventSessionIssue, {
        error: 'No available streaming providers',
      });

      return navigateToEventsPage({
        reason: LeaveEventReason.ExperienceFailure,
        message: 'noAvailableStreamingProviders',
        isModal: true,
      });
    }

    setStreamingProvider(streamingProvider);
    console.log(`Selected ${streamingProvider} pixel streaming provider`);

    if (pixelStreamingClient) {
      setPixelStreamingClient(pixelStreamingClient);

      console.log(
        `Pixel streaming client id: ${pixelStreamingClient.clientId}`,
      );
    }
  }, [currentEventId, userId, trackEventAnalytics]);

  useEffect(() => {
    return () => {
      if (loadPixelStreamingProviderTimeout.current) {
        clearTimeout(loadPixelStreamingProviderTimeout.current);
      }
    };
  }, []);

  return {
    streamingProvider,
    setStreamingProvider,

    pixelStreamingClient,
    setPixelStreamingClient,

    isNativeProviderCheckPending,
  };
};
